import { environment } from '@env/environment';
import { BaseModel } from "./base.model";
import { Building } from "./building.model";
import { Image } from './image.model';

export interface RangeValue {
  min: number | null;
  max: number | null;
}

export class Unit extends BaseModel {

  static TYPE_RESIDENTIAL = 'residential';
  static TYPE_BUSINESS = 'business';
  static TYPE_PARKING = 'parking';
  static TYPE_STORAGE = 'storage';

  static DESIGNATION_SELL = 'sell';
  static DESIGNATION_RENT = 'rent';

  static STATUS_FREE = 'available';
  static STATUS_RESERVED = 'reserved';
  static STATUS_RENTED = 'rented';

  static FITOUT_SHELL = 'shell';
  static FITOUT_FINISHED = 'finished';

  id?: number;
  propertyId!: string;
  instanceId?: number;
  buildingId!: number;
  buildingPropertyId!: string;
  perspectiveIndex?: number;

  images?: Image[];
  coverImageId?: number;
  coverImage?: {
    id: number;
    url: string;
  };

  floorplanId?: number;
  floorplan?: {
    id: number;
    url: string;
    mimeType?: string;
  };

  name!: string;
  description!: string;
  address!: string;
  type!: string;
  subtype?: string;
  designation!: string;
  roomNum!: string;
  numWorkstations?: number;
  fitoutStandard?: 'shell' | 'finished';
  floor!: number;
  totalSquareArea?: number;
  outdoorArea?: number;
  basementArea?: number;
  price!: string;
  priceNote?: string;
  auxiliaryCost?: number;
  minRentMonths?: number;
  status!: 'free' | 'reserved' | 'rented';
  tour3dLink?: string;
  configuratorLink?: string;
  applicationLink?: string;
  moveIn?: string;

  /* properties from building */
  applicationType?: string;

  createdBy?: number;
  updatedBy?: number;
  createdAt?: string;
  updatedAt?: string;

  get childModels() {
    return {
      images: Image,
    };
  }

  getIsForRent() {
    return this.designation === Unit.DESIGNATION_RENT;
  }

  getIsAvailable() {
    return this.status === Unit.STATUS_FREE;
  }

  isAvailable() {
    return this.status === Unit.STATUS_FREE;
  }

  getFitoutStandardLabel(): string {
    switch (this.fitoutStandard) {
      case Unit.FITOUT_SHELL:
        return 'Shell Area';
      case Unit.FITOUT_FINISHED:
        return 'Plug & Work';
      default:
        return 'N/A';
    }
  }

  getCoverImageUrl(spec: string = '') {

    if (this.coverImage?.id) {
      let url = `${environment.apiBaseUrl}/${environment.instanceUuid}/images/${this.coverImage.id}`;
      return spec ? `${url}?spec=${spec}` : url;
    }

    if (this.coverImage?.url) {
      return this.coverImage.url;
    }

    const defaultImages: Record<string, string> = {
      'residential': '/assets/img/icons/building.svg',
      'parking': '/assets/img/icons/car-cover.svg',
      'business': '/assets/img/icons/business.svg'
    };

    return defaultImages[this.type] || '/assets/img/icons/gallery.svg';
  }

  isExternalApplyForm() {
    return this.applicationType === Building.APPLICATION_TYPE_EXTERNAL;
  }

  hasFloorplan(): boolean {
    return !!(this.floorplanId || this['floorplan_id'] || this.floorplan);
  }

  has3dTour(): boolean {
    return !!(this.tour3dLink || this['tour_3d_link']);
  }

  get3dTourUrl(): string | null {
    return this.tour3dLink || this['tour_3d_link'] || null;
  }

  getFloorplanMimeType(): string | null {
    return this.floorplan?.mimeType ?? null;
  }

  getFloorplanUrl(): string | null {
    if (this.floorplan?.url) {
      return this.floorplan.url;
    }

    const floorplanId = this.floorplanId || this['floorplan_id'];

    if (floorplanId) {
      return `${environment.apiBaseUrl}/${environment.instanceUuid}/files/${floorplanId}`;
    }

    return null;
  }

  get isResidential(): boolean {
    return this.type === Unit.TYPE_RESIDENTIAL;
  }

  get isBusiness(): boolean {
    return this.type === Unit.TYPE_BUSINESS;
  }

  get priceNumeric(): number | null {
    if (!this.price) {
      return null;
    }

    const str = String(this.price).replace(/[^0-9.\-]/g, '');

    if (str.includes('-')) {
      return Number(str.split('-')[0]) || null;
    }

    const num = Number(str);
    return isNaN(num) || num === 0 ? null : num;
  }

  get auxiliaryCostNumeric(): number | null {
    // auxiliaryCost is typed as number, but decimal columns often come back from the
    // API as strings (e.g. "120.00") - coerce defensively rather than trust the type.
    if (this.auxiliaryCost === null || this.auxiliaryCost === undefined) {
      return null;
    }

    const num = Number(this.auxiliaryCost);
    return isNaN(num) || num === 0 ? null : num;
  }

  get totalPrice(): number | null {
    // price can be free text (e.g. "on request"), in which case priceNumeric is null
    // and there's nothing sensible to add auxiliaryCost to - don't show a gross figure.
    if (this.priceNumeric === null) {
      return null;
    }

    return this.priceNumeric + (this.auxiliaryCostNumeric ?? 0);
  }
}

interface StatusBreakdown {
  totalCount: number;
  totalSquareArea: number;
}

export type OccupationType = {
  type: string;
  totalCount: number;
  totalSquareArea: number;
  statuses: Record<string, StatusBreakdown>;
  subtypes: OccupationType[];
}

export class OccupationSummary extends BaseModel {

  title?: string;
  subtitle?: string;
  type: 'business' | 'residential' = 'business';

  coverImageId?: number;
  coverImage?: {
    id: number;
    url: string;
  };

  images?: any[];

  totalCount?: number;
  totalSquareArea?: number;
  statuses?: Record<string, StatusBreakdown>;

  occupation?: OccupationType[];

  getCoverImageUrl(spec: string = '') {

    if (this.coverImage?.id) {
      let url = `${environment.apiBaseUrl}/${environment.instanceUuid}/images/${this.coverImage.id}`;
      return spec ? `${url}?spec=${spec}` : url;
    }

    if (this.coverImage?.url) {
      return this.coverImage.url;
    }

    const defaultImages: Record<string, string> = {
      'business': '/assets/img/icons/building.svg',
      'residential': '/assets/img/icons/business.svg'
    };

    return defaultImages[this.type] || '/assets/img/icons/gallery.svg';
  }

  getOccupationBreakdown(): OccupationType[] {
    if (!this.occupation || this.occupation.length === 0) {
      return [];
    }

    if (this.occupation.length === 1) {
      return this.occupation[0].subtypes || [];
    }

    if (this.occupation.length > 1) {
      return this.occupation;
    }

    return [];
  }

  getTypeLabel(type: string) {
    switch (type) {
      /* UNIT TYPES */
      case 'business': return 'Business';
      case 'residential': return 'Residential';
      case 'parking': return 'Parking';
      /* UNIT SUBTYPES */
      case 'labor': return 'Labor';
      case 'office': return 'Office';
      case 'small_office': return 'Small office';
      case 'workshop': return 'Workshop';
      case 'warehouse': return 'Warehouse';
      case 'kitchen': return 'Kitchen';
      case 'gastronomy': return 'Gastronomy';

      case 'apartment': return 'Apartment';
      case 'house': return 'House';

      case 'parking_lot': return 'Parking lot';
      case 'garage': return 'Garage';

      default: return type;
    }
  }

}
